import React, { useState } from 'react';
import { 
  KeyIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import Dialog from '../common/Dialog';
import { User } from '../../services/users';

export interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onResetPassword: (userId: string, password?: string) => Promise<void>;
  loading?: boolean;
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  isOpen,
  onClose,
  user,
  onResetPassword,
  loading = false
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [resetMode, setResetMode] = useState<'auto' | 'manual'>('auto');
  const [confirmedResetMode, setConfirmedResetMode] = useState<'auto' | 'manual'>('auto');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const validatePassword = (): boolean => {
    if (resetMode === 'auto') {
      return true;
    }

    setPasswordError('');

    if (!password || password.trim() === '') {
      setPasswordError('Le mot de passe est requis');
      return false;
    }

    if (password.length < 8) {
      setPasswordError('Le mot de passe doit contenir au moins 8 caractères');
      return false;
    }

    if (password !== confirmPassword) {
      setPasswordError('Les mots de passe ne correspondent pas');
      return false;
    }

    // Vérifier la complexité du mot de passe
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

    if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
      setPasswordError('Le mot de passe doit contenir au moins une majuscule, une minuscule, un chiffre et un caractère spécial');
      return false;
    }

    return true;
  };

  const handleResetPassword = async () => {
    if (!user) return;

    if (resetMode === 'manual' && !validatePassword()) {
      return;
    }

    try {
      setIsSubmitting(true);
      const passwordToUse = resetMode === 'manual' ? password : undefined;
      await onResetPassword(user.id, passwordToUse);
      
      // Sauvegarder le mode utilisé pour l'affichage de confirmation
      setConfirmedResetMode(resetMode);
      setShowConfirmation(true);
      
      // Réinitialiser les champs
      setPassword('');
      setConfirmPassword('');
      setResetMode('auto');
      setPasswordError('');
      
      // Fermer automatiquement après 3 secondes
      setTimeout(() => {
        setShowConfirmation(false);
        onClose();
      }, 3000);
    } catch (error: any) {
      console.error('❌ Erreur lors de la réinitialisation du mot de passe:', error);
      alert(`Erreur lors de la réinitialisation: ${error.message || 'Erreur inconnue'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowConfirmation(false);
    setPassword('');
    setConfirmPassword('');
    setResetMode('auto');
    setPasswordError('');
    onClose();
  };

  const handleModeChange = (mode: 'auto' | 'manual') => {
    setResetMode(mode);
    setPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  if (!user) return null;

  if (showConfirmation) {
    return (
      <Dialog
        isOpen={isOpen}
        onClose={handleCancel}
        title="Mot de passe réinitialisé"
        size="md"
      >
        <div className="text-center space-y-6">
          <div className="flex justify-center">
            <div className="p-4 rounded-full bg-green-100">
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Mot de passe réinitialisé avec succès !
            </h3>
            <p className="text-sm text-gray-500">
              {confirmedResetMode === 'auto' ? (
                <>Un nouveau mot de passe a été généré et envoyé par email à <strong>{user.email}</strong></>
              ) : (
                <>Le mot de passe a été défini avec succès pour <strong>{user.name || user.username}</strong></>
              )}
            </p>
          </div>

          {confirmedResetMode === 'auto' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <EnvelopeIcon className="h-5 w-5 text-green-400 mr-2" />
                <p className="text-sm text-green-700">
                  <strong>Email envoyé :</strong> {user.email}
                </p>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 text-blue-400 mr-2 mt-0.5" />
              <div className="text-left">
                <p className="text-sm font-medium text-blue-800 mb-1">Informations importantes :</p>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• L'utilisateur devra se connecter avec le nouveau mot de passe</li>
                  {confirmedResetMode === 'auto' && (
                    <>
                      <li>• Le mot de passe temporaire expire après 24h</li>
                      <li>• L'utilisateur sera invité à changer son mot de passe</li>
                    </>
                  )}
                  {confirmedResetMode === 'manual' && (
                    <li>• Le mot de passe a été défini selon vos spécifications</li>
                  )}
                </ul>
              </div>
            </div>
          </div>

          <div className="pt-4">
            <button
              onClick={handleCancel}
              className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleCancel}
      title="Réinitialiser le mot de passe"
      size="md"
    >
      <div className="space-y-6">
        {/* Avertissement */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">
                Attention : Réinitialisation du mot de passe
              </h3>
              <p className="text-sm text-yellow-700 mt-1">
                Cette action va générer un nouveau mot de passe temporaire et l'envoyer par email à l'utilisateur.
              </p>
            </div>
          </div>
        </div>

        {/* Informations utilisateur */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <span className="text-orange-600 font-medium text-sm">
                {user.name?.split(' ').map((n: string) => n[0]).join('') || user.username?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">{user.name || 'Nom non défini'}</h3>
              <p className="text-sm text-gray-500">@{user.username}</p>
              {user.email && (
                <div className="flex items-center mt-1">
                  <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-1" />
                  <span className="text-sm text-gray-600">{user.email}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mode de réinitialisation */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Mode de réinitialisation :</h4>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => handleModeChange('auto')}
              className={`p-4 rounded-lg border-2 transition-all ${
                resetMode === 'auto'
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center mb-2">
                <div className={`w-4 h-4 rounded-full border-2 mr-2 ${
                  resetMode === 'auto' ? 'border-orange-500 bg-orange-500' : 'border-gray-300'
                }`}>
                  {resetMode === 'auto' && (
                    <div className="w-full h-full rounded-full bg-white scale-50"></div>
                  )}
                </div>
                <span className="font-medium text-gray-900">Automatique</span>
              </div>
              <p className="text-xs text-gray-600 text-left">
                Génération et envoi automatique par email
              </p>
            </button>
            <button
              type="button"
              onClick={() => handleModeChange('manual')}
              className={`p-4 rounded-lg border-2 transition-all ${
                resetMode === 'manual'
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center mb-2">
                <div className={`w-4 h-4 rounded-full border-2 mr-2 ${
                  resetMode === 'manual' ? 'border-orange-500 bg-orange-500' : 'border-gray-300'
                }`}>
                  {resetMode === 'manual' && (
                    <div className="w-full h-full rounded-full bg-white scale-50"></div>
                  )}
                </div>
                <span className="font-medium text-gray-900">Manuel</span>
              </div>
              <p className="text-xs text-gray-600 text-left">
                Saisie manuelle du mot de passe
              </p>
            </button>
          </div>
        </div>

        {/* Saisie manuelle du mot de passe */}
        {resetMode === 'manual' && (
          <div className="space-y-4 border-t border-gray-200 pt-4">
            <h4 className="font-medium text-gray-900">Nouveau mot de passe :</h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mot de passe <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError('');
                  }}
                  placeholder="Saisissez le nouveau mot de passe"
                  className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                    passwordError ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Minimum 8 caractères avec majuscule, minuscule, chiffre et caractère spécial
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirmer le mot de passe <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setPasswordError('');
                  }}
                  placeholder="Confirmez le nouveau mot de passe"
                  className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                    passwordError ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {passwordError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">{passwordError}</p>
              </div>
            )}
          </div>
        )}

        {/* Détails de l'opération */}
        {resetMode === 'auto' && (
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Ce qui va se passer :</h4>
            <div className="space-y-3">
              <div className="flex items-start">
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Génération d'un nouveau mot de passe</p>
                  <p className="text-xs text-gray-500">Mot de passe temporaire sécurisé et aléatoire</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Envoi par email</p>
                  <p className="text-xs text-gray-500">Le nouveau mot de passe sera envoyé à {user.email}</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Expiration automatique</p>
                  <p className="text-xs text-gray-500">Le mot de passe temporaire expire après 24h</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3"></div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Changement obligatoire</p>
                  <p className="text-xs text-gray-500">L'utilisateur devra changer son mot de passe à la prochaine connexion</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleCancel}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
            disabled={loading || isSubmitting}
          >
            Annuler
          </button>
          <button
            onClick={handleResetPassword}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || isSubmitting || (resetMode === 'manual' && (!password || !confirmPassword))}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Réinitialisation...
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <KeyIcon className="h-4 w-4 mr-2" />
                {resetMode === 'auto' ? 'Réinitialiser le mot de passe' : 'Définir le mot de passe'}
              </div>
            )}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default ResetPasswordModal;
